import { useEffect, useRef, useState, type RefObject } from "react";
import type Hls from "hls.js";
import { api } from "../api/endpoints";
import { errorMessage, isAbort } from "../api/client";
import { parseTs } from "../lib/time";

// Live view: POST /live, wait for the first playlist (404 until ready, retry
// every 1 s for up to 20 s), then play with native HLS (Safari) or hls.js.
// Shared by the home camera panel and the Cameras section.

const PLAYLIST_WAIT_MS = 20_000;
const STALL_MS = 10_000;

export type LivePhase =
  | { kind: "off" }
  | { kind: "starting" }
  | { kind: "waiting" }
  | { kind: "playing" }
  | { kind: "ended"; reason: string }
  | { kind: "error"; message: string };

let pendingStop: Promise<unknown> = Promise.resolve();

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(t);
      reject(new DOMException("aborted", "AbortError"));
    });
  });
}

async function waitForPlaylist(url: string, signal: AbortSignal): Promise<void> {
  const deadline = Date.now() + PLAYLIST_WAIT_MS;
  for (;;) {
    const res = await fetch(url, { cache: "no-store", signal });
    if (res.ok) return;
    if (res.status !== 404) throw new Error(`The stream failed to start (HTTP ${res.status}).`);
    if (Date.now() > deadline) throw new Error("The camera did not start streaming within 20 seconds. It may be asleep or out of Wi-Fi range.");
    await sleep(1000, signal);
  }
}

export interface LiveStream {
  videoRef: RefObject<HTMLVideoElement | null>;
  phase: LivePhase;
  startedAt: number;
  expiresAt: number | null;
  restart: () => void;
}

/** Runs a live view for `cameraId` while `active` is true; stops it (DELETE /live) when it goes false or unmounts. */
export function useLiveStream(cameraId: string | null, active: boolean): LiveStream {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [phase, setPhase] = useState<LivePhase>({ kind: "off" });
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!active || !cameraId) {
      setPhase({ kind: "off" });
      return;
    }
    const ctrl = new AbortController();
    const video = videoRef.current;
    let hls: Hls | null = null;
    let stallTimer: ReturnType<typeof setInterval> | null = null;
    let lastTime = -1;
    let lastProgressAt = Date.now();
    let everPlayed = false;
    const cleanups: Array<() => void> = [];

    const end = (reason: string) => {
      if (ctrl.signal.aborted) return;
      setPhase((p) => (p.kind === "error" ? p : { kind: "ended", reason }));
      hls?.destroy();
      hls = null;
      if (stallTimer) clearInterval(stallTimer);
    };
    const fail = (message: string) => {
      if (ctrl.signal.aborted) return;
      setPhase({ kind: "error", message });
    };
    const on = <K extends keyof HTMLVideoElementEventMap>(el: HTMLVideoElement, ev: K, fn: () => void) => {
      el.addEventListener(ev, fn);
      cleanups.push(() => el.removeEventListener(ev, fn));
    };

    const run = async () => {
      setPhase({ kind: "starting" });
      setStartedAt(Date.now());
      setExpiresAt(null);
      await pendingStop.catch(() => undefined);
      const session = await api.startLive(cameraId);
      if (ctrl.signal.aborted) return;
      setExpiresAt(parseTs(session.expires_at));
      setPhase({ kind: "waiting" });
      await waitForPlaylist(session.hls_url, ctrl.signal);
      if (!video || ctrl.signal.aborted) return;

      on(video, "playing", () => {
        everPlayed = true;
        setPhase({ kind: "playing" });
      });
      on(video, "ended", () => end("The live view ended. Blink limits how long a live view runs."));

      const nativeHls = video.canPlayType("application/vnd.apple.mpegurl") !== "";
      const apple = /Apple/.test(navigator.vendor);
      const HlsCtor = nativeHls && apple ? null : (await import("hls.js")).default;
      if (ctrl.signal.aborted) return;

      if (HlsCtor && HlsCtor.isSupported()) {
        hls = new HlsCtor({ lowLatencyMode: true, liveSyncDurationCount: 2, manifestLoadingMaxRetry: 3, levelLoadingMaxRetry: 3 });
        let mediaRecoveries = 0;
        hls.on(HlsCtor.Events.ERROR, (_e, data) => {
          if (!data.fatal) return;
          if (data.type === HlsCtor.ErrorTypes.MEDIA_ERROR && mediaRecoveries < 1) {
            mediaRecoveries++;
            hls?.recoverMediaError();
            return;
          }
          if (everPlayed) end("The live view ended.");
          else fail(`The stream could not be played (${data.details}).`);
        });
        hls.loadSource(session.hls_url);
        hls.attachMedia(video);
      } else if (nativeHls) {
        on(video, "error", () => (everPlayed ? end("The live view ended.") : fail("This browser could not play the stream.")));
        video.src = session.hls_url;
      } else {
        fail("This browser cannot play live video. Try Chrome, Edge, Firefox or Safari.");
        return;
      }
      video.muted = true;
      video.play().catch(() => {
        // Autoplay blocked: the native controls let the user start it.
      });

      // A live stream that stops advancing has ended even if nobody told us.
      stallTimer = setInterval(() => {
        if (!everPlayed || video.paused) {
          lastProgressAt = Date.now();
          return;
        }
        if (video.currentTime !== lastTime) {
          lastTime = video.currentTime;
          lastProgressAt = Date.now();
        } else if (Date.now() - lastProgressAt > STALL_MS) {
          end("The stream stopped sending video.");
        }
      }, 1000);
    };

    run().catch((err: unknown) => {
      if (isAbort(err) || ctrl.signal.aborted) return;
      fail(errorMessage(err));
    });

    const onPageHide = () => {
      void api.stopLive(cameraId).catch(() => undefined);
    };
    window.addEventListener("pagehide", onPageHide);

    return () => {
      ctrl.abort();
      window.removeEventListener("pagehide", onPageHide);
      cleanups.forEach((c) => c());
      if (stallTimer) clearInterval(stallTimer);
      hls?.destroy();
      if (video) {
        video.removeAttribute("src");
        video.load();
      }
      pendingStop = api.stopLive(cameraId).catch(() => undefined);
    };
  }, [cameraId, active, attempt]);

  return { videoRef, phase, startedAt, expiresAt, restart: () => setAttempt((a) => a + 1) };
}
